import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../auth';
import {
  transformSensorImages,
  transformSensorDevice,
  transformSensorsDailySummary,
  transformSensorsDailySummaryAdvanced,
} from '../../utils/sensors';
import { APIVersion } from '../../constants/keys';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import {
  SensorApi,
  SensorDailySummaries,
  SensorDailySummariesAdvanced,
  SensorDailySummariesApi,
  SensorDailySummariesImages,
  LocationApi,
  GpsCoordinates,
  NdviGddMetricsApi,
  AirMetricsApi,
  SoilMetricsApi,
  DeviceMetricsApi,
  DeviceMetrics,
  SensorSetting,
} from '../../types';

const transformErrorResponse = (response: FetchBaseQueryError) =>
  (response as { data?: { errors?: unknown } }).data?.errors;

interface GetSensorsArgs {
  query?: string;
  ordering?: string;
  page?: number;
  per_page?: number;
}

interface UpdateSensorArgs {
  sensorId: string;
  name: string;
}

interface CreateSensorArgs {
  serialNumber: string;
  name: string;
}

interface GetSensorsDailySummariesDatesArgs {
  crop_id?: string;
  date?: string;
  location_id?: string;
  query?: string;
  ordering?: string;
}

interface GetSensorsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SensorApi[];
}

interface GetSensorsDailySummariesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SensorDailySummariesApi[];
}

// Locations
interface GetLocationsArgs {
  crop_id?: string;
  install_date__lte?: string;
  ordering?: string;
  page?: number;
  per_page?: number;
  removal_date__gte?: string;
}
interface CreateLocationArgs {
  sensorId: string;
  name?: string;
  installPoint?: { type: string; coordinates: GpsCoordinates };
  installDate?: string;
  removalDate?: string;
}
interface EditLocationArgs extends CreateLocationArgs {
  locationId: string;
}
interface GetLocationsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LocationApi[];
}

// module for sensors & locations BE integration
export const sensorsApi = createApi({
  reducerPath: 'sensorsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    // Sensors
    getSensors: builder.query<SensorApi[], GetSensorsArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensors`,
        params,
      }),
      transformResponse: (data: GetSensorsResponse) => data.results,
    }),

    getSensorById: builder.query<SensorApi, { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensors/${id}`,
      }),
    }),

    updateSensor: builder.mutation<SensorApi, UpdateSensorArgs>({
      query: ({ sensorId, name }) => ({
        url: `/core-api/${APIVersion}/sensors/${sensorId}`,
        method: 'PATCH',
        body: { name },
      }),
      transformErrorResponse,
    }),

    createSensor: builder.mutation<SensorApi, CreateSensorArgs>({
      query: ({ serialNumber, name }) => ({
        url: `/core-api/${APIVersion}/sensors`,
        method: 'POST',
        body: { serial_number: serialNumber, name },
      }),
      transformErrorResponse,
    }),

    // Sensors daily summaries
    // Dashboard, Scoring
    getSensorsDailySummaries: builder.query<SensorDailySummaries[], GetSensorsArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensors-daily-summaries`,
        params,
      }),
      transformResponse: (data: GetSensorsDailySummariesResponse) => transformSensorsDailySummary(data.results),
    }),

    // Chart, Fields, Map, Sensor, Timeline
    getSensorsSummary: builder.query<SensorDailySummariesAdvanced[], GetSensorsArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensors-daily-summaries`,
        params,
      }),
      transformResponse: (data: GetSensorsDailySummariesResponse) => transformSensorsDailySummaryAdvanced(data.results),
    }),

    // Sensor images
    getSensorImages: builder.query<SensorDailySummariesImages, GetSensorsArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensors-daily-summaries`,
        params,
      }),
      transformResponse: (data: GetSensorsDailySummariesResponse) => transformSensorImages(data.results),
    }),

    getSensorsDates: builder.query<string[], GetSensorsDailySummariesDatesArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensors-daily-summaries/dates`,
        params,
      }),
    }),

    // Locations
    getLocations: builder.query<LocationApi[], GetLocationsArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensor-locations`,
        params,
      }),
      transformResponse: (data: GetLocationsResponse) => data.results,
    }),

    getLocation: builder.query<LocationApi, { id: string }>({
      query: ({ id }) => ({
        url: `core-api/${APIVersion}/sensor-locations/${id}`,
      }),
    }),

    createLocation: builder.mutation<LocationApi, CreateLocationArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensor-locations`,
        method: 'POST',
        body: {
          sensor_id: params.sensorId,
          name: params.name,
          install_point: params.installPoint,
          install_date: params.installDate,
          removal_date: params.removalDate,
        },
      }),
      transformErrorResponse,
    }),

    editLocation: builder.mutation<LocationApi, EditLocationArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/sensor-locations/${params.locationId}`,
        method: 'PATCH',
        body: {
          sensor_id: params.sensorId,
          name: params.name,
          install_point: params.installPoint,
          install_date: params.installDate,
          removal_date: params.removalDate,
        },
      }),
      transformErrorResponse,
    }),

    deleteLocation: builder.mutation<LocationApi, { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensor-locations/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),

    // Metrics
    getNdviGddMetrics: builder.query<NdviGddMetricsApi[], { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensor-locations/${id}/charts/ndvi-gdd`,
      }),
    }),

    getAirMetrics: builder.query<AirMetricsApi[], { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensor-locations/${id}/charts/air-metrics`,
      }),
    }),

    getSoilMetrics: builder.query<SoilMetricsApi[], { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensor-locations/${id}/charts/soil-metrics`,
      }),
    }),

    // Device
    getDeviceMetrics: builder.query<DeviceMetrics, { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensors/${id}/device`,
      }),
      transformResponse: (response: DeviceMetricsApi) => transformSensorDevice(response),
    }),

    // sensor-settings
    deleteSensorSettingsSoilSensor: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/sensor-settings/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),

    createSensorSettingsSoilSensor: builder.mutation<SensorSetting, { sensorId: string; serialNumber: string }>({
      query: ({ sensorId, serialNumber }) => ({
        url: `/core-api/${APIVersion}/sensor-settings`,
        method: 'POST',
        body: { sensor_id: sensorId, name: 'pair_soil_sensor', params: { serial_number: serialNumber } },
      }),
      transformErrorResponse,
    }),
  }),
});

export const {
  useGetSensorsQuery,
  useGetSensorByIdQuery,
  useUpdateSensorMutation,
  useCreateSensorMutation,

  useGetSensorsDailySummariesQuery,
  useGetSensorsSummaryQuery,
  useGetSensorsDatesQuery,
  useGetSensorImagesQuery,

  useGetLocationsQuery,
  useGetLocationQuery,
  useCreateLocationMutation,
  useEditLocationMutation,
  useDeleteLocationMutation,

  useGetNdviGddMetricsQuery,
  useGetAirMetricsQuery,
  useGetSoilMetricsQuery,
  useGetDeviceMetricsQuery,

  useCreateSensorSettingsSoilSensorMutation,
  useDeleteSensorSettingsSoilSensorMutation,
} = sensorsApi;
